import { z } from 'zod';
import { DISCO_CODE } from '../data/discoData';

export const DiscoEnum = z.enum(DISCO_CODE, { error: 'disco s required' });

const registrationInfoSchema = z.object({
  incorporation_date: z.string({ error: 'Select valid incorporation date' }),
  registered_office: z
    .string({ error: 'Enter valid registered office' })
    .trim(),
});

const AutoCompleteOptionSchema = (message: string) =>
  z.object(
    {
      id: z.string(),
      name: z.string(),
    },
    { message }
  );

const newAddressSchema = z.object({
  street: z.string().min(1, 'Street address is required'),
  street_line_2: z.string().optional(),
  city: z.string().min(1, 'City is required'),
  state: AutoCompleteOptionSchema('state is required'),
  country: AutoCompleteOptionSchema('Country is required'),
  postal_code: z
    .string()
    .optional()
    .refine((val) => !val || val.length >= 3, {
      message: 'Postal code must be at least 3 characters',
    }),

  lga: z
    .string()
    .min(1, 'Local Government Area is required')
    .optional()
    .or(z.literal('')),
  // This allows LGA to be optional but forces a message if it's partially filled
});

const addressChoiceSchema = z.union([
  z.object({
    type: z.literal('new'),
    new_address: newAddressSchema,
  }),
  z.object({
    type: z.literal('saved'),
    saved_address_id: z.number(),
  }),
]);

const getDocumentSchema = (fileName: string) =>
  z.instanceof(File, { message: `${fileName} is required` });

const kycDocuments = z.object({
  cac: getDocumentSchema('CAC certificate'),
  tin: getDocumentSchema('TIN document'),
  board_resolution: getDocumentSchema('Board resolution'),
  memart: getDocumentSchema('Memorandum and Articles'),
  utility_bill: getDocumentSchema('Utility bill'),
  scuml: getDocumentSchema('SCUML document'),
});

export const walletKycDirectorSchema = z.object({
  first_name: z
    .string({
      message: 'Director first name is required',
    })
    .min(1, { message: 'Director first name is required' })
    .toLowerCase(),
  last_name: z
    .string({
      message: 'Director last name is required',
    })
    .min(1, { message: 'Director last name is required' })
    .toLowerCase(),
  middle_name: z
    .string({
      message: 'Director middle name is required',
    })
    .toLowerCase()
    .optional()
    .nullable(),
  email: z
    .email({ message: 'A valid Director email is required' })
    .trim()
    .toLowerCase(),
  phone_number: z
    .string({
      message: 'Director phone number is required',
    })
    .min(1, { message: 'Director phone number is required' }),
  role: z
    .enum(['director', 'owner', 'manager', 'secretary'], {
      message: 'Role must be one of: director, owner, manager, secretary',
    })
    .optional(),
  date_of_birth: z
    .string('Date of birth is required')
    .min(1, 'Date of birth is required'), // Ensures it's not an empty string
  percentage_ownership: z
    .number({ message: 'Percentage ownership must be a number' })
    .optional(),
  nin: z
    .string({ message: 'NIN is required' })
    .trim()
    .max(11, { message: 'A valid NIN is required' }),
  bvn: z
    .string({ message: 'BVN is required' })
    .trim()
    .max(11, { message: 'A valid BVN is required' }),
  address: z
    .string({
      message: 'Director address is required',
    })
    .min(1, { message: 'Director address is required' }),
  meter_number: z.string('meter number is required').trim(),
  disco_code: DiscoEnum,
});

export const walletKycSchema = z.object({
  customer_id: z.number(), // JUST SO IT CAN BE ADDED AS FORM WIZARD PROGRESSES
  business_name: z.string({ message: 'Store name is required' }).trim(),
  business_registration_number: z
    .string({
      message: 'Store registration number is required',
    })
    // .min(6, {
    //   message: 'Enter valid registration number',
    // })
    // .max(6, {
    //   message: 'Enter valid registration number',
    // })
    .trim(),
  business_type: z.string({ message: 'Store type is required' }),
  wallet_preferred_name: z
    .string({ message: 'Enter a valid name' })
    .trim()
    .optional(),
  email: z.email({ message: 'Store email is required' }).trim().toLowerCase(),
  phone: z.string({ message: 'Store phone number is required' }).trim(),
  tin: z
    .string({ message: 'TIN is required' })
    .max(11, { message: 'A valid TIN is required' }),
  // kyc_documents: kycDocument,
  registration_info: registrationInfoSchema,
  address_choice: addressChoiceSchema,
  directors: z
    .array(walletKycDirectorSchema)
    .min(1, { error: 'Please add at least one director to proceed.' }),
  documents: kycDocuments,
});

export type WalletKyc = z.infer<typeof walletKycSchema>;
export default walletKycSchema;
